/**
 * The phone recorder's one camera and microphone stream (spec 13.6).
 *
 * Safari asked for the camera and the microphone again and again because the
 * recorder asked for them separately (the bubble, the microphone at every
 * take, a camera source on open) and stopped each one after use. On a phone
 * there is now one request, for both at once, made the first time the coach
 * records or turns the bubble on, and one stream kept for the whole editor
 * session: every take, segment, rotation and save reuses it, and nothing
 * stops its tracks until the coach leaves the editor (releaseCapture).
 *
 * If iOS ends the tracks (a call, Siri, another app taking the camera), the
 * next acquireCapture asks once more. The desktop recorder does not use this.
 */

let stream: MediaStream | null = null;
let pending: Promise<MediaStream> | null = null;
const listeners = new Set<(s: MediaStream | null) => void>();

function isLive(s: MediaStream): boolean {
  const tracks = s.getTracks();
  return tracks.length > 0 && tracks.every((t) => t.readyState === 'live');
}

/** The stream if it is still live, without asking for anything. */
export function currentCapture(): MediaStream | null {
  return stream && isLive(stream) ? stream : null;
}

/**
 * The stream, asking for camera and microphone together only when there is
 * no live one. Concurrent callers share the one request.
 */
export function acquireCapture(): Promise<MediaStream> {
  const live = currentCapture();
  if (live) return Promise.resolve(live);
  if (pending) return pending;
  if (stream) {
    // Ended under us: let it go and ask once more.
    stream.getTracks().forEach((t) => t.stop());
    stream = null;
  }
  pending = navigator.mediaDevices
    .getUserMedia({
      video: true,
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    })
    .then((s) => {
      stream = s;
      listeners.forEach((fn) => fn(s));
      return s;
    })
    .finally(() => { pending = null; });
  return pending;
}

/** Stops the tracks. Only when the coach leaves the editor. */
export function releaseCapture(): void {
  if (!stream) return;
  stream.getTracks().forEach((t) => t.stop());
  stream = null;
  listeners.forEach((fn) => fn(null));
}

/** Told when a new stream arrives (or the old one is released), so elements can re-attach. */
export function onCaptureChange(fn: (s: MediaStream | null) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
